import { useCallback, useEffect, useState } from 'react';
import {
  persistCanvasPanelVisibility,
  resolveInitialCanvasPanelOpen,
} from './canvasPanelVisibilityStorage.js';

const firstTruthy = (...readers) => readers.reduce((result, read) => result || read(), undefined);
const everyTruthy = (...readers) => readers.reduce((result, read) => result && read(), true);

function isWorkspaceToggleShortcut(event) {
  const key = String(firstTruthy(() => (Object(event).key), () => (''))).trim().toLowerCase();
  const code = String(firstTruthy(() => (Object(event).code), () => (''))).trim();
  return Boolean(
    everyTruthy(() => (firstTruthy(() => (Object(event).metaKey), () => (Object(event).ctrlKey))), () => (Object(event).shiftKey === true), () => (Object(event).altKey !== true), () => (firstTruthy(() => (key === ' '), () => (key === 'spacebar'), () => (code === 'Space'))))
  );
}

export function usePanels({ onCanvasPanelClosed } = {}) {
  const [knowledgeHubPopoverOpen, setKnowledgeHubPopoverOpen] = useState(false);
  const [showCanvasPanel, setShowCanvasPanelState] = useState(() => resolveInitialCanvasPanelOpen());
  const [canvasActiveTabKey, setCanvasActiveTabKeyState] = useState('');
  const [canvasTargetFocusKey, setCanvasTargetFocusKey] = useState(0);
  const [canvasFocusEvidenceAnchorId, setCanvasFocusEvidenceAnchorId] = useState('');

  const setShowCanvasPanel = useCallback((nextValue) => {
    setShowCanvasPanelState((previous) => (
      typeof nextValue === 'function'
        ? nextValue(previous) === true
        : nextValue === true
    ));
  }, []);

  const handleOpenKnowledgeHub = useCallback(() => {
    setKnowledgeHubPopoverOpen(true);
  }, []);

  const setCanvasActiveTabKey = useCallback((nextValue) => {
    setCanvasTargetFocusKey((current) => current + 1);
    setCanvasActiveTabKeyState((previous) => (
      typeof nextValue === 'function' ? nextValue(previous) : nextValue
    ));
  }, []);

  const handleToggleWorkspacePanel = useCallback(() => {
    onCanvasPanelClosed?.();
    setShowCanvasPanel((prev) => !prev);
  }, [onCanvasPanelClosed, setShowCanvasPanel]);

  const handleCloseWorkspacePanel = useCallback(() => {
    setShowCanvasPanel(false);
  }, [setShowCanvasPanel]);

  useEffect(() => {
    if (showCanvasPanel) {
      return;
    }
    onCanvasPanelClosed?.();
  }, [onCanvasPanelClosed, showCanvasPanel]);

  useEffect(() => {
    persistCanvasPanelVisibility(showCanvasPanel);
  }, [showCanvasPanel]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const handleKeyDown = (event) => {
      if (event?.repeat || event?.isComposing || !isWorkspaceToggleShortcut(event)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      handleToggleWorkspacePanel();
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [handleToggleWorkspacePanel]);

  const handleUICardAction = useCallback((action) => {
    const actionType = String(firstTruthy(() => (Object(action).type), () => (Object(action).action_key), () => (''))).trim();
    if (actionType === 'open_evidence_citation') {
      const citationId = String(firstTruthy(() => (Object(action).citation_id), () => (''))).trim();
      setShowCanvasPanel(true);
      setCanvasActiveTabKey('evidence');
      setCanvasFocusEvidenceAnchorId(citationId ? `retrieval-evidence-${citationId}` : '');
      return;
    }
    if (firstTruthy(() => (actionType.includes('canvas')), () => (actionType.includes('evidence')), () => (actionType.includes('sourcing')))) {
      setShowCanvasPanel(true);
      setCanvasActiveTabKey(actionType.includes('sourcing') ? 'sourcing' : 'evidence');
    }
  }, [setShowCanvasPanel, setCanvasActiveTabKey]);

  useEffect(() => {
    if (!canvasFocusEvidenceAnchorId) {
      return;
    }
    const timer = setTimeout(() => setCanvasFocusEvidenceAnchorId(''), 1600);
    return () => clearTimeout(timer);
  }, [canvasFocusEvidenceAnchorId]);

  return {
    knowledgeHubPopoverOpen,
    setKnowledgeHubPopoverOpen,
    showCanvasPanel,
    setShowCanvasPanel,
    canvasActiveTabKey,
    setCanvasActiveTabKey,
    canvasTargetFocusKey,
    canvasFocusEvidenceAnchorId,
    setCanvasFocusEvidenceAnchorId,
    handleOpenKnowledgeHub,
    handleToggleWorkspacePanel,
    handleCloseWorkspacePanel,
    handleUICardAction,
  };
}

export default usePanels;
